<script lang="ts">
  import { signup } from '../lib/auth';
  import { ApiError } from '../lib/api';

  interface Props {
    onAuthed: () => void;
    onWantLogin: () => void;
    /** Pre-filled invite token (e.g. from /invitasjon/<token>). Sent on signup. */
    inviteToken?: string;
  }
  let { onAuthed, onWantLogin, inviteToken }: Props = $props();

  let email = $state('');
  let password = $state('');
  let confirm = $state('');
  let error: string | null = $state(null);
  let busy = $state(false);
  let recoveryCode: string | null = $state(null);

  async function submit(e: SubmitEvent) {
    e.preventDefault();
    error = null;
    if (password.length < 12) {
      error = 'Passordet må være minst 12 tegn. (Det er den eneste måten å hente dataene dine på — uten det trenger du gjenopprettingskoden.)';
      return;
    }
    if (password !== confirm) {
      error = 'Passordene må være like.';
      return;
    }
    busy = true;
    try {
      const { recoveryCode: code } = await signup(
        email.trim().toLowerCase(),
        password,
        inviteToken,
      );
      recoveryCode = code;
      password = '';
      confirm = '';
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        error = 'Den eposten er allerede i bruk.';
      } else if (err instanceof ApiError && err.status === 403) {
        const detail = err.detail as { error?: string } | null;
        if (detail?.error === 'signup_closed') {
          error = 'Selvregistrering er ikke åpen. Du trenger en invitasjonslenke.';
        } else if (detail?.error === 'invalid_invite') {
          error = 'Invitasjonslenken er ugyldig eller allerede brukt.';
        } else {
          error = 'Kontooppretting feilet.';
        }
      } else {
        error = 'Kontooppretting feilet.';
      }
    } finally {
      busy = false;
    }
  }
</script>

{#if recoveryCode}
  <section class="card" aria-labelledby="rc-h">
    <h2 id="rc-h">Skriv ned gjenopprettingskoden din</h2>
    <p>
      Denne koden lar deg låse opp dataene dine om du glemmer passordet. Vi
      lagrer den <strong>ikke</strong> på serveren — skriver du ikke ned
      koden nå, vil dataene være borte for godt om passordet forsvinner.
    </p>
    <code class="recovery-code" aria-label="Gjenopprettingskode">{recoveryCode}</code>
    <button class="primary" onclick={onAuthed}>Jeg har skrevet den ned</button>
  </section>
{:else}
  <form onsubmit={submit} class="card" aria-labelledby="signup-h">
    <h2 id="signup-h">Opprett konto</h2>
    {#if inviteToken}
      <!-- role="status" lets AT users hear the context as a soft announcement
           rather than have it buried in document order as a plain div. -->
      <div class="banner" role="status">Du registrerer deg med en invitasjonslenke.</div>
    {/if}
    <p class="muted">
      Vi krypterer alt du markerer som <em>privat</em> i nettleseren din. Serveren
      ser aldri passordet ditt eller innholdet i private oppføringer.
      <a href="/personvern" target="_blank" rel="noopener noreferrer">Les hvordan det virker.</a>
    </p>

    <label for="signup-email">Epost</label>
    <input id="signup-email" type="email" autocomplete="username" bind:value={email} required />

    <label for="signup-password">Passord (minst 12 tegn)</label>
    <input id="signup-password" type="password" autocomplete="new-password"
           minlength="12" bind:value={password} required />

    <label for="signup-confirm">Bekreft passord</label>
    <input id="signup-confirm" type="password" autocomplete="new-password"
           minlength="12" bind:value={confirm} required />

    {#if error}<p class="error" role="alert">{error}</p>{/if}

    <div class="row" style="margin-top: 1rem;">
      <button class="primary" type="submit" disabled={busy}>
        {busy ? 'Oppretter …' : 'Opprett konto'}
      </button>
      <button type="button" onclick={onWantLogin}>Jeg har allerede en konto</button>
    </div>
  </form>
{/if}
